// src/pages/PropertiesPage.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  Building2,
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  MapPin,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Navigation,
  FileText,
  TrendingUp,
  MoreVertical,
  Map,
  AlertCircle,
  X,
} from 'lucide-react';
import { useTheme } from '../theme/ThemeContext';
import { useNavigate } from 'react-router-dom';

// ✅ Auth helpers
const getAuthToken = () =>
  localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

const getAuthRole = () =>
  localStorage.getItem('authRole') || sessionStorage.getItem('authRole');

const PropertiesPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState([]);

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 🔹 Stats from backend
  const [stats, setStats] = useState({
    totalProperties: 0,
    activeProperties: 0,
    totalVisits: 0,
    avgChecklist: 0,
  });

  // 🔹 View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);

  // ✅ Common: clear auth + redirect
  const logoutAndRedirect = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authRole');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authRole');
    navigate('/');
  };

  // ---- FETCH FROM BACKEND ----
  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      setError('');

      const token = getAuthToken();
      const role = getAuthRole();

      if (!token || role !== 'admin') {
        setError('Session expired. Please log in again.');
        logoutAndRedirect();
        return;
      }

      try {
        const url = `https://patrol-visit.patroll-management.nissiindia.com/api/properties`;

        const res = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          if (res.status === 401) {
            setError('Session expired. Please log in again.');
            logoutAndRedirect();
            return;
          }
          throw new Error('Failed to fetch properties');
        }

        const data = await res.json();
        const list = Array.isArray(data.data) ? data.data : [];

        setProperties(list);

        // 🔹 Fallback stats if backend doesn't send them
        const fallbackTotalProperties = list.length;
        const fallbackActiveProperties = list.filter(
          (p) => (p.status || 'active') === 'active'
        ).length;
        const fallbackTotalVisits = list.reduce(
          (sum, p) => sum + (p.totalVisits || 0),
          0
        );
        const fallbackAvgChecklist =
          fallbackTotalProperties === 0
            ? 0
            : list.reduce(
                (sum, p) => sum + (p.checklistItems || 0),
                0
              ) / fallbackTotalProperties;

        setStats({
          totalProperties: data.stats?.totalProperties ?? fallbackTotalProperties,
          activeProperties:
            data.stats?.activeProperties ?? fallbackActiveProperties,
          totalVisits: data.stats?.totalVisits ?? fallbackTotalVisits,
          avgChecklist:
            data.stats?.avgChecklist ??
            Number(fallbackAvgChecklist.toFixed(1)),
        });
      } catch (err) {
        console.error('Error fetching properties:', err);
        setError(err.message || 'Failed to fetch properties');
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [navigate]);

  // ---- STATUS BADGE ----
  const getStatusBadge = (status) => {
    const config = {
      active: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
      inactive: { bg: 'bg-slate-100', text: 'text-slate-700', icon: XCircle },
      pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
    };

    const { bg, text, icon: Icon } = config[status] || config.active;

    return (
      <span
        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${bg} ${text}`}
      >
        <Icon className="w-3 h-3" />
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Active'}
      </span>
    );
  };

  // ---- FILTERED LIST ----
  const filteredProperties = useMemo(() => {
    return properties.filter((property) => {
      const q = searchQuery.toLowerCase();

      const matchesSearch =
        property.name?.toLowerCase().includes(q) ||
        property.client?.toLowerCase().includes(q) ||
        property.address?.toLowerCase().includes(q) ||
        property.city?.toLowerCase().includes(q);

      const matchesStatus =
        statusFilter === 'all' ||
        (property.status || 'active') === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [properties, searchQuery, statusFilter]);

  // ---- STATS CARDS (from backend stats) ----
  const statsCards = [
    {
      title: 'Total Properties',
      value: String(stats.totalProperties ?? 0),
      change: '+0%',
      icon: Building2,
    },
    {
      title: 'Active Properties',
      value: String(stats.activeProperties ?? 0),
      change: '+0%',
      icon: CheckCircle,
    },
    {
      title: 'Total Visits',
      value: String(stats.totalVisits ?? 0),
      change: '+0%',
      icon: TrendingUp,
    },
    {
      title: 'Avg. Checklist Items',
      value: String(stats.avgChecklist ?? 0),
      change: '+0',
      icon: FileText,
    },
  ];

  // 🔹 Open property in Google Maps (lat/lng preferred, otherwise address)
  const openPropertyOnMap = (property) => {
    if (!property) return;

    const lat = property.latitude;
    const lng = property.longitude;

    let url = '';

    if (lat && lng) {
      url = `https://www.google.com/maps?q=${encodeURIComponent(
        `${lat},${lng}`
      )}`;
    } else {
      const parts = [
        property.addressLine1 || property.address,
        property.city,
        property.state,
        property.pincode,
        property.country,
      ].filter(Boolean);

      if (!parts.length) return;

      const query = encodeURIComponent(parts.join(', '));
      url = `https://www.google.com/maps/search/?api=1&query=${query}`;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // 🔹 FETCH SINGLE PROPERTY FOR VIEW MODAL
  const handleViewClick = async (id) => {
    const token = getAuthToken();
    const role = getAuthRole();

    if (!token || role !== 'admin') {
      setViewError('Session expired. Please log in again.');
      logoutAndRedirect();
      return;
    }

    setViewLoading(true);
    setViewError('');
    setSelectedProperty(null);
    setViewModalOpen(true);

    try {
      const res = await fetch(`https://patrol-visit.patroll-management.nissiindia.com/api/properties/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('Property not found');
        }
        if (res.status === 401) {
          setViewError('Session expired. Please log in again.');
          logoutAndRedirect();
          return;
        }
        throw new Error('Failed to fetch property details');
      }

      const data = await res.json();
      const prop = data.data || data;
      setSelectedProperty(prop);
    } catch (err) {
      console.error('Error fetching property:', err);
      setViewError(err.message || 'Failed to load property details');
    } finally {
      setViewLoading(false);
    }
  };

  // 🔹 CLOSE VIEW MODAL
  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedProperty(null);
    setViewError('');
  };

  // 🔹 DELETE HANDLER (row & from modal)
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this property?')) {
      return;
    }

    const token = getAuthToken();
    const role = getAuthRole();

    if (!token || role !== 'admin') {
      alert('Session expired. Please log in again.');
      logoutAndRedirect();
      return;
    }

    try {
      const res = await fetch(`https://patrol-visit.patroll-management.nissiindia.com/api/properties/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        if (res.status === 401) {
          alert('Session expired. Please log in again.');
          logoutAndRedirect();
          return;
        }
        throw new Error('Failed to delete property');
      }

      setProperties((prev) => prev.filter((p) => p.id !== id));
      setSelectedProperties((prev) => prev.filter((pid) => pid !== id));
      if (selectedProperty && selectedProperty.id === id) {
        closeViewModal();
      }
    } catch (err) {
      alert(err.message || 'Failed to delete property');
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0"
              style={{
                background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.secondary[600]} 100%)`,
              }}
            >
              <Building2 className="w-5 h-5 sm:w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-800 truncate">
                Property Management
              </h1>
              <p className="text-xs sm:text-sm text-slate-600 mt-1 truncate">
                Manage patrol locations and property details
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <button
              onClick={() => navigate('/properties/add')}
              className="flex items-center justify-center gap-2 px-4 py-2 text-white rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all duration-200 text-sm w-full sm:w-auto"
              style={{
                background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.secondary[600]} 100%)`,
              }}
            >
              <Plus className="w-4 h-4" />
              <span>Add Property</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error & Loading */}
      {error && (
        <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-4 text-sm text-red-700">
          {error}
        </div>
      )}
      {loading && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 text-sm text-slate-600">
          Loading properties...
        </div>
      )}

      {/* Stats Grid (uses backend visit stats) */}
      {!loading && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {statsCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-slate-100 hover:scale-105 cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div
                    className="p-2 sm:p-3 rounded-lg sm:rounded-xl group-hover:scale-110 transition-transform"
                    style={{
                      backgroundColor: `${theme.primary[50]}`,
                    }}
                  >
                    <Icon
                      className="w-4 h-4 sm:w-6 sm:h-6"
                      style={{ color: theme.primary[600] }}
                    />
                  </div>
                  <span className="text-xs font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full bg-emerald-100 text-emerald-700">
                    {stat.change}
                  </span>
                </div>
                <h3 className="text-slate-600 text-xs sm:text-sm font-medium mb-1 line-clamp-1">
                  {stat.title}
                </h3>
                <p className="text-2xl sm:text-3xl font-bold text-slate-800">
                  {stat.value}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6">
        <div className="flex flex-col gap-3 sm:gap-4">
          {/* Search Bar */}
          <div className="w-full">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search properties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 sm:py-3 border-2 border-slate-200 rounded-xl focus:ring-4 outline-none transition-all text-sm sm:text-base"
                style={{
                  borderColor: searchQuery ? theme.primary[500] : 'rgb(226 232 240)',
                  '--tw-ring-color': `${theme.primary[500]}33`,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = theme.primary[500];
                }}
                onBlur={(e) => {
                  if (!searchQuery) {
                    e.target.style.borderColor = 'rgb(226 232 240)';
                  }
                }}
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 sm:gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-slate-200 rounded-xl focus:ring-4 outline-none transition-all bg-white text-sm sm:text-base"
              style={{
                '--tw-ring-color': `${theme.primary[500]}33`,
              }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2.5 sm:p-3 border-2 rounded-xl transition-all flex-shrink-0 ${
                showFilters
                  ? 'bg-opacity-10'
                  : 'border-slate-200 hover:bg-slate-50'
              }`}
              style={
                showFilters
                  ? {
                      borderColor: theme.primary[500],
                      backgroundColor: `${theme.primary[50]}`,
                    }
                  : {}
              }
            >
              <Filter className="w-5 h-5 text-slate-600" />
            </button>
          </div>
        </div>

        {/* Advanced Filters (static for now) */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">
                Property Type
              </label>
              <select className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:outline-none text-sm">
                <option>All Types</option>
                <option>Commercial</option>
                <option>Residential</option>
                <option>Industrial</option>
                <option>Mixed-use</option>
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">
                City
              </label>
              <select className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:outline-none text-sm">
                <option>All Cities</option>
                <option>Mumbai</option>
                <option>Chennai</option>
                <option>Bangalore</option>
                <option>Hyderabad</option>
              </select>
            </div>

            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">
                Assigned Guard
              </label>
              <select className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:outline-none text-sm">
                <option>All Guards</option>
                <option>John Smith</option>
                <option>Sarah Johnson</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Properties Table / Cards */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base sm:text-lg font-bold text-slate-800">
                All Properties
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 mt-1">
                Showing {filteredProperties.length} of {properties.length} properties
              </p>
            </div>

            {selectedProperties.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs sm:text-sm text-slate-600">
                  {selectedProperties.length} selected
                </span>
                <button
                  onClick={() => {
                    selectedProperties.forEach((id) => handleDelete(id));
                  }}
                  className="px-2 sm:px-3 py-1 text-xs sm:text-sm text-red-600 hover:bg-red-50 rounded-lg transition-all"
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="block lg:hidden divide-y divide-slate-100">
          {filteredProperties.map((property) => (
            <div key={property.id} className="p-4 hover:bg-slate-50 transition-colors">
              <div className="flex items-start gap-3 mb-3">
                <input
                  type="checkbox"
                  checked={selectedProperties.includes(property.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedProperties([...selectedProperties, property.id]);
                    } else {
                      setSelectedProperties(
                        selectedProperties.filter((id) => id !== property.id)
                      );
                    }
                  }}
                  className="w-4 h-4 rounded border-slate-300 mt-1 flex-shrink-0"
                />

                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3 mb-2">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{
                        background: `linear-gradient(135deg, ${theme.primary[100]}, ${theme.secondary[100]})`,
                      }}
                    >
                      <Building2
                        className="w-5 h-5"
                        style={{ color: theme.primary[600] }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-slate-800 text-sm mb-1 line-clamp-2">
                        {property.name}
                      </h4>
                      {getStatusBadge(property.status || 'active')}
                    </div>
                  </div>

                  <div className="space-y-2 text-xs">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Users className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{property.client}</span>
                    </div>

                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">
                        {property.city}, {property.state}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 flex-wrap">
                      <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-blue-100 text-blue-700">
                        {property.propertyType}
                      </span>
                      <span className="flex items-center gap-1 text-slate-600">
                        <FileText className="w-3 h-3" />
                        {property.checklistItems || 0} items
                      </span>
                      <span className="flex items-center gap-1 text-slate-600">
                        <TrendingUp className="w-3 h-3" />
                        {property.totalVisits || 0} visits
                      </span>
                    </div>

                    <div className="flex items-center gap-1 text-slate-500">
                      <Clock className="w-3 h-3" />
                      Last visit (IST): {property.lastVisit || '—'}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                    <button
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-slate-50 hover:bg-slate-100 rounded-lg transition-all text-xs font-medium text-slate-700"
                      onClick={() => handleViewClick(property.id)}
                    >
                      <Eye className="w-3 h-3" />
                      View
                    </button>
                    <button
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-slate-50 hover:bg-slate-100 rounded-lg transition-all text-xs font-medium text-slate-700"
                      onClick={() => navigate(`/properties/add/${property.id}`)}
                    >
                      <Edit className="w-3 h-3" />
                      Edit
                    </button>
                    <button
                      className="flex items-center justify-center p-2 bg-slate-50 hover:bg-slate-100 rounded-lg transition-all"
                      onClick={() => openPropertyOnMap(property)}
                    >
                      <Map className="w-4 h-4 text-slate-600" />
                    </button>
                    <button
                      className="flex items-center justify-center p-2 hover:bg-red-50 rounded-lg transition-all"
                      onClick={() => handleDelete(property.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {!loading && filteredProperties.length === 0 && (
            <div className="p-4 text-xs text-slate-500">No properties found.</div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-slate-300"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedProperties(properties.map((p) => p.id));
                      } else {
                        setSelectedProperties([]);
                      }
                    }}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Property
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Checklist
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Visits
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredProperties.map((property) => (
                <tr key={property.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedProperties.includes(property.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProperties([...selectedProperties, property.id]);
                        } else {
                          setSelectedProperties(
                            selectedProperties.filter((id) => id !== property.id)
                          );
                        }
                      }}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{
                          background: `linear-gradient(135deg, ${theme.primary[100]}, ${theme.secondary[100]})`,
                        }}
                      >
                        <Building2
                          className="w-5 h-5"
                          style={{ color: theme.primary[600] }}
                        />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">
                          {property.name}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Last visit (IST): {property.lastVisit || '—'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-700">
                        {property.client}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-1 text-sm text-slate-700">
                        <MapPin className="w-3 h-3 text-slate-400" />
                        <span>
                          {property.city}, {property.state}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Navigation className="w-3 h-3" />
                        <span>
                          GPS: {property.latitude || '—'},{' '}
                          {property.longitude || '—'}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-blue-100 text-blue-700">
                      {property.propertyType}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="font-semibold text-slate-800">
                        {property.checklistItems || 0}
                      </span>
                      <span className="text-xs text-slate-500">items</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-semibold text-slate-800">
                      {property.totalVisits || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(property.status || 'active')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        className="p-2 hover:bg-slate-100 rounded-lg transition-all"
                        title="View Details"
                        onClick={() => handleViewClick(property.id)}
                      >
                        <Eye className="w-4 h-4 text-slate-600" />
                      </button>
                      <button
                        className="p-2 hover:bg-slate-100 rounded-lg transition-all"
                        title="Edit Property"
                        onClick={() => navigate(`/properties/add?id=${property.id}`)}
                      >
                        <Edit className="w-4 h-4 text-slate-600" />
                      </button>
                      <button
                        className="p-2 hover:bg-slate-100 rounded-lg transition-all"
                        title="View on Map"
                        onClick={() => openPropertyOnMap(property)}
                      >
                        <Map className="w-4 h-4 text-slate-600" />
                      </button>
                      <button
                        className="p-2 hover:bg-red-50 rounded-lg transition-all"
                        title="Delete Property"
                        onClick={() => handleDelete(property.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                      <button className="p-2 hover:bg-slate-100 rounded-lg transition-all">
                        <MoreVertical className="w-4 h-4 text-slate-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {!loading && filteredProperties.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-6 py-4 text-sm text-slate-500 text-center"
                  >
                    No properties found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination (static for now) */}
        <div className="px-4 sm:px-6 py-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs sm:text-sm text-slate-600">
            Showing 1 to {filteredProperties.length} of {properties.length} results
          </p>
          <div className="flex items-center gap-2">
            <button className="px-2 sm:px-3 py-1 border border-slate-200 rounded-lg text-xs sm:text-sm text-slate-600 hover:bg-slate-50 transition-all">
              Previous
            </button>
            <button
              className="px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm text-white font-medium"
              style={{
                background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.secondary[600]} 100%)`,
              }}
            >
              1
            </button>
            <button className="px-2 sm:px-3 py-1 border border-slate-200 rounded-lg text-xs sm:text-sm text-slate-600 hover:bg-slate-50 transition-all">
              2
            </button>
            <button className="px-2 sm:px-3 py-1 border border-slate-200 rounded-lg text-xs sm:text-sm text-slate-600 hover:bg-slate-50 transition-all">
              Next
            </button>
          </div>
        </div>
      </div>

      {/* 🔹 VIEW MODAL */}
      {viewModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 p-4 sm:p-6 relative">
            {/* Close button */}
            <button
              onClick={closeViewModal}
              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-100"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>

            <div className="flex items-center gap-3 mb-4 border-b border-slate-100 pb-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${theme.primary[100]}, ${theme.secondary[100]})`,
                }}
              >
                <Building2
                  className="w-5 h-5"
                  style={{ color: theme.primary[600] }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-slate-800 truncate">
                  {selectedProperty?.propertyName ||
                    selectedProperty?.property_name ||
                    selectedProperty?.name ||
                    'Property'}
                </h2>
                <p className="text-xs text-slate-500">
                  Property details, checklist & last visit (IST)
                </p>
              </div>
            </div>

            {viewLoading && (
              <p className="text-sm text-slate-600">Loading property details...</p>
            )}

            {viewError && (
              <p className="text-sm text-red-600 flex items-center gap-1 mb-2">
                <AlertCircle className="w-4 h-4" />
                {viewError}
              </p>
            )}

            {!viewLoading && selectedProperty && (
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                {/* Basic info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Client</p>
                    <p className="text-sm font-semibold text-slate-800">
                      {selectedProperty.client ||
                        selectedProperty.client_name ||
                        `Client #${
                          selectedProperty.clientId || selectedProperty.client_id
                        }`}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Property Type</p>
                    <p className="text-sm font-semibold text-slate-800">
                      {selectedProperty.propertyType ||
                        selectedProperty.property_type ||
                        '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Status</p>
                    <div className="mt-1">
                      {getStatusBadge(selectedProperty.status || 'active')}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Total Visits</p>
                    <p className="text-sm font-semibold text-slate-800">
                      {selectedProperty.totalVisits ||
                        selectedProperty.total_visits ||
                        0}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Last Visit (IST)</p>
                    <p className="text-sm font-semibold text-slate-800">
                      {selectedProperty.lastVisit ||
                        selectedProperty.last_visit_label ||
                        '—'}
                    </p>
                  </div>
                </div>

                {/* Address */}
                <div className="border border-slate-100 rounded-xl p-3">
                  <p className="text-xs font-semibold text-slate-600 mb-1">
                    Address
                  </p>
                  <p className="text-sm text-slate-800">
                    {selectedProperty.addressLine1 ||
                      selectedProperty.address_line1 ||
                      '—'}
                    {selectedProperty.addressLine2 ||
                    selectedProperty.address_line2
                      ? `, ${
                          selectedProperty.addressLine2 ||
                          selectedProperty.address_line2
                        }`
                      : ''}
                  </p>
                  <p className="text-sm text-slate-800">
                    {selectedProperty.city || '—'}
                    {selectedProperty.state
                      ? `, ${selectedProperty.state}`
                      : ''}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Pincode:{' '}
                    {selectedProperty.pincode || '—'} • Country:{' '}
                    {selectedProperty.country || '—'}
                  </p>
                </div>

                {/* GPS */}
                <div className="border border-slate-100 rounded-xl p-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-slate-600 mb-1">
                      GPS Location
                    </p>
                    <p className="text-xs text-slate-700">
                      Lat:{' '}
                      {selectedProperty.latitude || '—'} • Lng:{' '}
                      {selectedProperty.longitude || '—'}
                    </p>
                  </div>
                </div>

                {/* Checklist preview */}
                <div className="border border-slate-100 rounded-xl p-3">
                  <p className="text-xs font-semibold text-slate-600 mb-2">
                    Checklist ({(selectedProperty.checklist || []).length})
                  </p>
                  {(selectedProperty.checklist || []).length === 0 ? (
                    <p className="text-xs text-slate-500">
                      No checklist items configured.
                    </p>
                  ) : (
                    <ul className="space-y-1">
                      {selectedProperty.checklist.slice(0, 5).map((item, idx) => (
                        <li
                          key={item.id || idx}
                          className="text-xs text-slate-700 flex items-center gap-2"
                        >
                          <span className="w-4 h-4 rounded-full bg-emerald-100 text-[10px] flex items-center justify-center text-emerald-700">
                            {idx + 1}
                          </span>
                          <span>{item.item}</span>
                        </li>
                      ))}
                      {selectedProperty.checklist.length > 5 && (
                        <li className="text-[11px] text-slate-500">
                          + {selectedProperty.checklist.length - 5} more items
                        </li>
                      )}
                    </ul>
                  )}
                </div>

                {/* Notes */}
                {selectedProperty.notes && (
                  <div className="border border-slate-100 rounded-xl p-3">
                    <p className="text-xs font-semibold text-slate-600 mb-1">
                      Notes
                    </p>
                    <p className="text-xs text-slate-700 whitespace-pre-line">
                      {selectedProperty.notes}
                    </p>
                  </div>
                )}

                {/* Modal actions */}
                <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (!selectedProperty) return;
                      const id = selectedProperty.id;
                      closeViewModal();
                      navigate(`/properties/add/${id}`);
                    }}
                    className="w-full sm:w-auto px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold text-white flex items-center justify-center gap-2 shadow-sm hover:shadow-lg transition-all"
                    style={{
                      background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.secondary[600]} 100%)`,
                    }}
                  >
                    <Edit className="w-4 h-4" />
                    Edit Property
                  </button>
                  <button
                    type="button"
                    onClick={closeViewModal}
                    className="w-full sm:w-auto px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertiesPage;
